import DOMPurify from 'dompurify';

interface RichTextViewerProps {
  content: string;
  className?: string;
}

/**
 * Safely renders HTML content with sanitization to prevent XSS attacks.
 * Uses Tailwind Typography (prose) for consistent styling.
 */
export function RichTextViewer({
  content,
  className = '',
}: RichTextViewerProps) {
  if (!content) return null;

  // Sanitize HTML to prevent XSS attacks
  // DOMPurify removes dangerous elements like <script>, onclick handlers, etc.
  const sanitizedHtml = DOMPurify.sanitize(content, {
    // Allow common formatting tags
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      'blockquote',
      'pre',
      'code',
      'span',
      'div',
    ],
    // Allow safe attributes
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel'],
    // Force all links to open in new tab safely
    ADD_ATTR: ['target', 'rel'],
    // Transform hooks for additional safety
    ALLOW_DATA_ATTR: false,
  });

  // Add safe attributes to links
  const processedHtml = sanitizedHtml.replace(
    /<a /g,
    '<a target="_blank" rel="noopener noreferrer" ',
  );

  return (
    <div
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
}
